import axios from "axios";

// 개발 환경에서는 프록시 사용, 프로덕션에서는 직접 API 호출
const API_ENDPOINT =
  process.env.NODE_ENV === "development"
    ? "/api"
    : process.env.REACT_APP_API_ENDPOINT ||
      "https://56wsxfetk7.execute-api.ap-northeast-2.amazonaws.com/prod";

if (process.env.NODE_ENV === "development") {
  console.log("현재 환경:", process.env.NODE_ENV);
  console.log("API_ENDPOINT:", API_ENDPOINT);
}

const api = axios.create({
  baseURL: API_ENDPOINT,
  headers: {
    "Content-Type": "application/json",
  },
});

// 요청 인터셉터
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    if (token) {
      console.log("API 요청에 토큰 추가:", token.substring(0, 10) + "...");
      config.headers.Authorization = `Bearer ${token}`;
    } else {
      console.log("토큰 없음, 인증 헤더 추가하지 않음");
    }

    // 요청 데이터가 객체인 경우 JSON 문자열로 변환
    if (config.data && typeof config.data === "object") {
      config.data = JSON.stringify(config.data);
    }

    console.log("API 요청 헤더:", config.headers);
    console.log("API 요청 URL:", config.url);

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// 응답 인터셉터
api.interceptors.response.use(
  (response) => {
    console.log("응답 인터셉터 - 원본 응답:", response);

    // API Gateway 응답 구조 처리
    if (response.data && response.data.statusCode) {
      console.log("API Gateway 응답 구조 감지");
      if (response.data.statusCode >= 400) {
        const errorData = JSON.parse(response.data.body);
        return Promise.reject({
          response: {
            data: errorData,
            status: response.data.statusCode,
          },
        });
      }
      // 성공 응답의 경우 body를 파싱하여 반환
      const parsedData = JSON.parse(response.data.body);
      console.log("파싱된 응답 데이터:", parsedData);
      return {
        ...response,
        data: parsedData,
      };
    }

    // 일반 응답 구조 처리
    console.log("일반 응답 구조 처리");
    return response;
  },
  async (error) => {
    console.error("API Error:", error.response?.data || error.message);
    if (error.response?.status === 401) {
      localStorage.removeItem("token");
      window.location.href = "/login";
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register: async (userData) => {
    const response = await api.post("/auth/register", userData);
    return response.data;
  },

  verifyEmail: async (verificationData) => {
    const response = await api.post("/auth/verify-email", verificationData);
    return response.data;
  },

  login: async (credentials) => {
    const response = await api.post("/auth/login", credentials);
    return response.data;
  },

  logout: async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        console.log("로그아웃: 토큰이 이미 없음");
        return { success: true };
      }

      // 서버에 로그아웃 요청 보내기
      const response = await api.post("/auth/logout", { token });
      console.log("로그아웃 응답:", response);

      // 성공 여부와 상관없이 로컬 스토리지에서 토큰 제거
      localStorage.removeItem("token");
      console.log("로컬 스토리지에서 토큰 제거됨");

      return response.data;
    } catch (error) {
      console.error("로그아웃 처리 중 오류:", error);
      // 오류가 발생해도 로컬 스토리지는 정리
      localStorage.removeItem("token");
      console.log("오류 발생했지만 로컬 스토리지에서 토큰 제거됨");

      return { success: true, message: "로컬에서만 로그아웃됨" };
    }
  },

  getUserInfo: async () => {
    const token = localStorage.getItem("token");
    console.log("localStorage에 저장된 토큰:", token);

    if (!token) {
      console.error("토큰이 없습니다. 로그인이 필요합니다.");
      throw new Error("토큰이 없습니다.");
    }

    console.log("토큰이 존재합니다. API 요청을 진행합니다.");
    try {
      const response = await api.get("/auth/me");
      console.log("getUserInfo 응답:", response);
      return response.data;
    } catch (error) {
      console.error("getUserInfo API 호출 실패:", error);
      throw error;
    }
  },

  extendSession: async () => {
    const response = await api.post("/auth/extend");
    return response.data;
  },

  sendVerificationCode: async (email) => {
    const response = await api.post("/auth/send-code", {
      email,
    });
    return response.data;
  },

  checkVerificationCode: async (verificationData) => {
    const response = await api.post("/auth/verify-code", verificationData);
    return response.data;
  },

  goRegister: async (userData) => {
    // 이미 이메일 인증이 완료되었는지 확인
    try {
      // 회원가입 처리
      const response = await api.post("/auth/register", userData);
      return response.data;
    } catch (error) {
      console.error("회원가입 처리 중 오류:", error);
      throw error;
    }
  },

  post: async (url, data) => {
    try {
      const response = await api.post(url, data);
      return response;
    } catch (error) {
      console.error("API Error:", error.response?.data || error.message);
      throw error;
    }
  },

  googleLogin: async (googleData) => {
    try {
      console.log("1. Google 로그인 요청 데이터:", googleData);

      const response = await api.post("/auth/google", {
        accessToken: googleData.credential,
        email: googleData.email,
        name: googleData.name,
        googleId: googleData.googleId,
      });

      console.log("2. API 응답:", response);

      if (!response || !response.data) {
        throw new Error("로그인 응답이 올바르지 않습니다.");
      }

      return response.data;
    } catch (error) {
      console.error("Google 로그인 에러:", error);
      throw error;
    }
  },
};

// 프로젝트 관련 API
export const projectAPI = {
  // 새 프로젝트 생성
  createProject: async (projectData) => {
    try {
      // 토큰 확인
      const token = localStorage.getItem("token");
      if (!token) {
        throw new Error("인증 토큰이 없습니다. 로그인이 필요합니다.");
      }

      // 요청 데이터에 토큰 포함
      const dataWithToken = {
        ...projectData,
        token: token, // 토큰을 요청 본문에 포함
      };

      console.log("프로젝트 생성 요청 데이터:", {
        ...dataWithToken,
        token: token.substring(0, 10) + "...", // 로깅 시 토큰 일부만 표시
      });

      // API 호출 (절대 URL 사용)
      const response = await axios.post(
        `${API_ENDPOINT}/project/create`,
        dataWithToken,
        {
          headers: {
            "Content-Type": "application/json",
          },
        }
      );

      console.log("프로젝트 생성 응답:", response.data);
      return response.data;
    } catch (error) {
      console.error("프로젝트 생성 오류:", error);
      throw error;
    }
  },

  // 사용자의 프로젝트 목록 조회
  getUserProjects: async () => {
    try {
      const response = await api.get("/user/projects");
      return response.data;
    } catch (error) {
      console.error("프로젝트 목록 조회 API 오류:", error);
      throw error;
    }
  },

  // 초대 코드로 프로젝트 참여
  joinProject: async (invitationCode) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) throw new Error("인증 토큰이 없습니다.");

      const response = await api.post("/project/join", {
        invitationCode,
        token, // fallback for Lambda if header stripped
      });
      return response.data;
    } catch (error) {
      console.error("프로젝트 참여 API 오류:", error);
      throw error;
    }
  },
};

export const formAPI = {
  saveForm: async (formData) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");

      const response = await fetch(`${API_ENDPOINT}/edc-save`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw {
          message: "API 요청 실패",
          response: { data: errorData },
          status: response.status,
        };
      }

      return await response.json();
    } catch (error) {
      console.error("formAPI.saveForm 오류:", error);
      throw error;
    }
  },

  // VIP 프로젝트 데이터 전송
  sendVipData: async (formData, projectId, caseId = null) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) throw new Error("인증 토큰이 없습니다.");

      const requestData = {
        formData: formData,
        projectId: projectId,
        caseId: caseId,
      };

      console.log("VIP 데이터 전송 요청:", requestData);

      const response = await fetch(`${API_ENDPOINT}/edc/send-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(requestData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "데이터 전송 실패");
      }

      const result = await response.json();
      console.log("VIP 데이터 전송 응답:", result);

      // Lambda 응답 구조 처리
      if (result.statusCode) {
        if (result.statusCode >= 400) {
          const errorData = JSON.parse(result.body);
          throw new Error(errorData.message || "데이터 저장 실패");
        }
        // 성공 응답의 경우 body를 파싱하여 반환
        const parsedData = JSON.parse(result.body);
        console.log("파싱된 응답 데이터:", parsedData);
        return parsedData;
      }

      // 일반 응답 구조인 경우 그대로 반환
      return result;
    } catch (error) {
      console.error("formAPI.sendVipData 오류:", error);
      throw error;
    }
  },

  // 프로젝트의 폼 버전 목록 조회
  getFormVersions: async (projectId) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");

      const response = await fetch(
        `${API_ENDPOINT}/form-versions?project_id=${projectId}`,
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error("버전 목록을 가져오는데 실패했습니다.");
      }

      return await response.json();
    } catch (error) {
      console.error("formAPI.getFormVersions 오류:", error);
      throw error;
    }
  },

  // 특정 버전의 폼 구조 조회
  getFormVersion: async (versionId) => {
    try {
      const response = await api.get(`/form-versions/${versionId}`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("폼 버전 조회 중 오류:", error);
      throw error;
    }
  },

  // 새 버전 저장
  saveFormVersion: async (formData) => {
    try {
      const response = await api.post(`/form-versions`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("폼 버전 저장 중 오류:", error);
      throw error;
    }
  },

  // 폼 버전 삭제
  deleteFormVersion: async (versionId, projectId) => {
    try {
      const response = await api.delete(`/edc/form-versions/${versionId}`, {
        params: { project_id: projectId },
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
      });
      return response.data;
    } catch (error) {
      console.error("폼 버전 삭제 중 오류:", error);
      throw error;
    }
  },

  // 폼 구조 정보 가져오기
  getFormStructure: async (projectId) => {
    try {
      console.log("API getFormStructure 호출 - projectId:", projectId);
      const response = await api.get(`/edc/get-form`, {
        params: { project_id: projectId },
      });
      console.log("getFormStructure 응답:", response);
      console.log("getFormStructure 데이터:", response.data);
      return response.data;
    } catch (error) {
      console.error("폼 구조 조회 API 오류:", error);
      console.error("오류 응답:", error.response?.data);
      console.error("오류 상태:", error.response?.status);
      throw error;
    }
  },

  // SubjectList 컬럼 설정 저장 (edc/setting-subjectlist)
  saveSubjectListSetting: async (projectId, subjectColumns) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) throw new Error("인증 토큰이 없습니다.");

      const response = await fetch(`${API_ENDPOINT}/edc/setting-subjectlist`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ projectId, subjectColumns }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "설정 저장 실패");
      }

      return await response.json();
    } catch (error) {
      console.error("formAPI.saveSubjectListSetting 오류:", error);
      throw error;
    }
  },

  // SubjectList 데이터 조회 (edc/get-subjectlist)
  getSubjectList: async (projectId, opts = {}) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) throw new Error("인증 토큰이 없습니다.");

      const url = `${API_ENDPOINT}/edc/get-subjectlist`;

      // projectId 가 인자로 없으면 sessionStorage 에서 시도
      const finalProjectId = projectId || sessionStorage.getItem("project_id");
      if (!finalProjectId) throw new Error("projectId가 필요합니다.");

      const body = { projectId: finalProjectId, token };
      if (opts.startDate) body.startDate = opts.startDate;
      if (opts.endDate) body.endDate = opts.endDate;

      const response = await fetch(url, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "데이터 조회 실패");
      }

      return await response.json();
    } catch (error) {
      console.error("formAPI.getSubjectList 오류:", error);
      throw error;
    }
  },

  getFormDataById: async (dataId) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const url = `${API_ENDPOINT}/edc/get-formdata?dataId=${encodeURIComponent(
      dataId
    )}`;
    const response = await fetch(url, {
      method: "GET",
      headers: { Authorization: `Bearer ${token}` },
    });
    if (!response.ok) throw new Error("데이터 조회 실패");
    return await response.json();
  },

  updateFormData: async (dataId, formData) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    if (!token) throw new Error("인증 토큰이 없습니다.");
    const url = `${API_ENDPOINT}/edc/update-data`;
    const response = await fetch(url, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify({ dataId, formData }),
    });
    if (!response.ok) throw new Error("데이터 수정 실패");
    return await response.json();
  },

  // DQT 조건 저장 (edc/setting-conditions)
  saveConditionSetting: async (projectId, conditions) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) throw new Error("인증 토큰이 없습니다.");

      const response = await fetch(`${API_ENDPOINT}/edc/setting-conditions`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId, conditions, token }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "조건 저장 실패");
      }

      return await response.json();
    } catch (error) {
      console.error("formAPI.saveConditionSetting 오류:", error);
      throw error;
    }
  },

  // 저장된 DQT 조건 조회 (GET)
  getConditionSetting: async (projectId) => {
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) throw new Error("인증 토큰이 없습니다.");

      const url = `${API_ENDPOINT}/edc/get-conditions?projectId=${encodeURIComponent(
        projectId
      )}&token=${encodeURIComponent(token)}`;

      const response = await fetch(url, { method: "GET" });

      if (!response.ok) {
        const err = await response.json();
        throw new Error(err.message || "조건 조회 실패");
      }

      return await response.json();
    } catch (error) {
      console.error("formAPI.getConditionSetting 오류:", error);
      throw error;
    }
  },

  getFilteredSubjects: async (projectId, conditions) => {
    const token =
      localStorage.getItem("token") || sessionStorage.getItem("token");
    const res = await fetch(`${API_ENDPOINT}/edc/get-subjects-by-condition`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ projectId, conditions, token }),
    });
    if (!res.ok) throw new Error("조회 실패");
    return (await res.json()).rows || [];
  },

  // 특정 변수들만 포함한 데이터 조회 (VIP Dashboard 용)
  // 백엔드에 별도 엔드포인트가 없다면, 전체 SubjectList 를 가져온 뒤
  // 필요한 변수만 추려서 반환한다.
  getVariableData: async (projectId, variableIds = []) => {
    try {
      const rowsRes = await formAPI.getSubjectList(projectId);
      // 백엔드가 { rows: [...] } 형태를 반환하는 경우도 처리
      const rows = Array.isArray(rowsRes)
        ? rowsRes
        : Array.isArray(rowsRes?.rows)
        ? rowsRes.rows
        : [];

      // variableIds 가 비어 있으면 그대로 반환
      if (!variableIds?.length) return rows;

      // 각 row 에 대해 필요한 변수만 남긴 새 객체 생성
      return rows.map((row) => {
        const filtered = {};
        variableIds.forEach((vid) => {
          // 1) row 최상위에 값이 있으면 사용
          if (row.hasOwnProperty(vid)) {
            filtered[vid] = row[vid];
          }
          // 2) form_data 내부에 값이 있으면 사용
          else if (row.form_data && row.form_data.hasOwnProperty(vid)) {
            filtered[vid] = row.form_data[vid];
          }
        });
        // created_at 등 타임스탬프는 대시보드 통계에 활용될 수 있으므로 보존
        if (row.created_at) filtered.created_at = row.created_at;
        if (row.createdAt) filtered.createdAt = row.createdAt;
        if (row.date) filtered.date = row.date;
        // 사이트 정보 likewise
        const src = row.form_data || row;
        if (src.arrest_site) filtered.arrest_site = src.arrest_site;
        if (src.site) filtered.site = src.site;
        if (src.arrest_location) filtered.arrest_location = src.arrest_location;
        return filtered;
      });
    } catch (error) {
      console.error("formAPI.getVariableData 오류:", error);
      throw error;
    }
  },

  // 특정 변수 데이터만 Lambda를 통해 가져오기 (edc/get-variable)
  getVariableDataLambda: async (projectId, variableIds = []) => {
    if (!projectId || !variableIds.length) {
      throw new Error("projectId와 variableIds가 필요합니다.");
    }
    try {
      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) throw new Error("인증 토큰이 없습니다.");

      const response = await fetch(`${API_ENDPOINT}/edc/get-variable`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ projectId, variables: variableIds, token }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.message || "변수 데이터 조회 실패");
      }

      const result = await response.json();
      const rows = Array.isArray(result)
        ? result
        : Array.isArray(result?.rows)
        ? result.rows
        : [];
      return rows;
    } catch (error) {
      console.error("formAPI.getVariableDataLambda 오류:", error);
      throw error;
    }
  },

  // 새로 추가: 프로젝트에서 현재 사용자 user_value 조회
  getUserValue: async (projectId) => {
    try {
      if (!projectId) throw new Error("projectId가 필요합니다.");

      const token =
        localStorage.getItem("token") || sessionStorage.getItem("token");
      if (!token) throw new Error("인증 토큰이 없습니다.");

      const response = await fetch(`${API_ENDPOINT}/edc/user-value-check`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ projectId, token }), // token은 body에도 fallback 전송
      });

      if (!response.ok) {
        const err = await response.json().catch(() => null);
        throw new Error(err?.message || "user_value 조회 실패");
      }

      const result = await response.json();

      // Lambda 가 API Gateway 원형으로 돌려온 경우(body/statusCode 포함)
      if (result?.statusCode) {
        if (result.statusCode >= 400) {
          const err = JSON.parse(result.body);
          throw new Error(err?.message || "user_value 조회 실패");
        }
        return JSON.parse(result.body); // { success, projectId, userId, userValue }
      }

      return result; // { success, projectId, userId, userValue }
    } catch (error) {
      console.error("formAPI.getUserValue 오류:", error);
      throw error;
    }
  },

  // 데이터 건수 조회 API
  getDataNumber: async (projectId) => {
    try {
      console.log("API call getDataNumber with projectId:", projectId);
      const response = await fetch(`${API_ENDPOINT}/edc/get-data-number`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ projectId }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        console.error("API Error Response:", errorData);
        throw new Error(errorData.message || "데이터 조회에 실패했습니다");
      }

      const data = await response.json();
      console.log("API Success Response:", data);

      // API Gateway 응답 구조 처리
      if (data.statusCode) {
        if (data.statusCode >= 400) {
          const errorBody = JSON.parse(data.body);
          throw new Error(errorBody.message || "데이터 조회에 실패했습니다");
        }
        return JSON.parse(data.body);
      }

      return data;
    } catch (error) {
      console.error("getDataNumber API Error:", error);
      throw error;
    }
  },

  // 새로운 API 함수 추가
  getData: async (params) => {
    try {
      const {
        projectId,
        search = "",
        searchFields,
        startIndex = 0,
        rowPerPage = 15,
      } = params;

      if (!projectId) throw new Error("projectId가 필요합니다.");

      const requestData = {
        projectId,
        search,
        searchFields: searchFields || ["hos_id", "data_id"],
        startIndex,
        rowPerPage,
      };

      console.log("[getData] 요청 데이터:", requestData);

      // POST 방식으로 요청
      const response = await api.post("/edc/get-data", requestData);

      // 응답 로깅
      if (response.data) {
        console.log("[getData] 응답 데이터:", response.data);
      }

      return response.data;
    } catch (error) {
      // 상세 에러 로깅
      console.error("[getData] API 오류:", {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status,
        requestData: error.config?.data,
      });

      // Lambda 응답 구조 처리 시도
      if (error.response?.data?.body) {
        try {
          const errorBody = JSON.parse(error.response.data.body);
          throw new Error(errorBody.message || "서버 오류가 발생했습니다.");
        } catch (e) {
          // JSON 파싱 실패 시 원본 에러 사용
          throw error;
        }
      }

      throw error;
    }
  },

  // 프로젝트 데이터 다운로드
  downloadData: async (projectId, format, formKeys) => {
    try {
      const response = await fetch(`${API_ENDPOINT}/edc/download-data`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          projectId,
          format,
          formKeys,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || "데이터 다운로드 실패");
      }

      const result = await response.json();
      return result;
    } catch (error) {
      console.error("formAPI.downloadData 오류:", error);
      throw error;
    }
  },
};

export default api;
